import React, { useRef, useEffect, useState, useCallback } from "react";
import { widget } from "../../charting_library";
import PropTypes from "prop-types";
import "./index.scss";

const TVChartContainer = (props) => {
  const {
    symbol,
    transactions,
    openPositions,
    visibleRange,
    resetVisibleRange,
  } = props;

  const ref = useRef();
  const [tvWidget, setTvWidget] = useState(null);
  const positions = useRef([]);
  const symbolRef = useRef(symbol);
  const transactionsRef = useRef(transactions);
  const openPositionsRef = useRef(openPositions);

  useEffect(() => {
    symbolRef.current = symbol;
  }, [symbol]);

  useEffect(() => {
    transactionsRef.current = transactions;
  }, [transactions]);

  useEffect(() => {
    openPositionsRef.current = openPositions;
  }, [openPositions]);

  const isIntradayResolution = (resolution) =>
    !!resolution && !/[DWM]/i.test(String(resolution));

  // Daily bars can snap a midnight timestamp onto the previous candle depending on TZ.
  // Intraday (e.g. 1m) must keep the exact time.
  const toExecutionTime = (dateValue, resolution) => {
    const date = new Date(dateValue);
    if (!isIntradayResolution(resolution)) {
      date.setHours(23, 0, 0, 0);
    }
    return Math.floor(date.getTime() / 1000);
  };

  const createExecutionShape = (tvWidgetInstance, order, type, isOpen) => {
    let direction = type;
    let text = "Buy";
    let color = "#00c4ff";
    const resolution = tvWidgetInstance.activeChart().resolution();
    let time = Math.floor(new Date(order.openDate).setHours(23) / 1000);

    let price = order.openRate;
    let tooltip = "";

    if (type === "sell") {
      text = `Sell: ${order.profit.perc} %`;
      color = order.profit.perc > 0 ? "#0F0" : "#ff5900";
      time = toExecutionTime(order.closeDate, resolution);
      price = order.closeRate;
      tooltip = `Duration: ${order.dealDaysOpen} bars`;
    }
    if (isOpen) {
      text = "Buy *Active";
    }

    positions.current.push(
      tvWidgetInstance
        .activeChart()
        .createExecutionShape({ disableUndo: true })
        .setText(text)
        .setTextColor(color)
        .setArrowColor(color)
        .setArrowHeight(15)
        .setArrowSpacing(5)
        .setFont("12px Verdana")
        .setTooltip(tooltip)
        .setDirection(direction)
        .setTime(time)
        .setPrice(price),
    );
  };

  const showTransactionsOnChart = useCallback((tvWidgetInstance) => {
    if (!tvWidgetInstance) return;

    positions.current.forEach((item) => {
      item?.remove();
    });
    positions.current = [];

    const currentSymbol = symbolRef.current;
    transactionsRef.current?.forEach((order) => {
      if (order.symbol !== currentSymbol) return;
      createExecutionShape(tvWidgetInstance, order, "buy");
      if (order.orderStatus === "closed") {
        createExecutionShape(tvWidgetInstance, order, "sell");
      }
    });
    if (openPositionsRef.current?.length > 0) {
      openPositionsRef.current.forEach((order) => {
        createExecutionShape(tvWidgetInstance, order, "buy", true);
      });
    }
  }, []);

  useEffect(() => {
    // change visible range
    if (tvWidget && visibleRange?.from) {
      tvWidget
        .activeChart()
        .setVisibleRange({ from: visibleRange.from, to: visibleRange.to });
      resetVisibleRange();
    }
  }, [tvWidget, visibleRange?.from]);

  useEffect(() => {
    let debounce;
    if (tvWidget && symbol) {
      debounce = setTimeout(() => {
        showTransactionsOnChart(tvWidget);
      }, 1000);
    }
    return () => clearTimeout(debounce);
  }, [tvWidget, transactions, symbol, openPositions, showTransactionsOnChart]);

  useEffect(() => {
    if (!tvWidget) return undefined;

    const chart = tvWidget.activeChart();
    const onIntervalChanged = () => {
      // Wait for the new resolution bars to load before redrawing markers
      setTimeout(() => {
        showTransactionsOnChart(tvWidget);
      }, 500);
    };

    chart.onIntervalChanged().subscribe(null, onIntervalChanged);

    return () => {
      try {
        chart.onIntervalChanged().unsubscribe(null, onIntervalChanged);
      } catch (_) {
        // chart may already be disposed
      }
    };
  }, [tvWidget, showTransactionsOnChart]);

  useEffect(() => {
    if (tvWidget && symbol) {
      tvWidget.setSymbol(symbol, tvWidget.symbolInterval().interval);
    }
  }, [tvWidget, symbol]);

  const onChartReady = (tvWidgetInstance) => {
    setTvWidget(tvWidgetInstance);
  };

  const onMount = () => {
    const widgetOptions = {
      symbol,
      // BEWARE: no trailing slash is expected in feed URL
      datafeed: new window.Datafeeds.UDFCompatibleDatafeed(
        props.datafeedUrl,
        3000,
      ),
      interval: props.interval,
      container: ref.current,
      library_path: props.libraryPath,

      locale: "en",
      // disabled_features: ["use_localstorage_for_settings"],
      enabled_features: ["pre_post_market_sessions"],
      // debug: true,
      client_id: props.clientId,
      user_id: props.userId,
      timezone: props.timezone,
      fullscreen: props.fullscreen,
      autosize: props.autosize,
      theme: props.theme,
      studies_overrides: props.studiesOverrides,
    };

    const tvWidgetInstance = new widget(widgetOptions);
    tvWidgetInstance.onChartReady(() => onChartReady(tvWidgetInstance));
  };

  const onUnmount = () => {
    if (tvWidget !== null) {
      tvWidget.remove();
      setTvWidget(null);
    }
  };
  useEffect(() => {
    onMount();

    return () => {
      onUnmount();
    };
  }, []);

  return <div ref={ref} className="TVChartContainer" />;
};

TVChartContainer.propTypes = {
  symbol: PropTypes.string,
  transactions: PropTypes.array,
  openPositions: PropTypes.array,
};

TVChartContainer.defaultProps = {
  symbol: "SOXL",
  interval: "1D",
  datafeedUrl: "/api/tv_chart",
  libraryPath: "/charting_library/",
  chartsStorageUrl: "https://saveload.tradingview.com",
  chartsStorageApiVersion: "1.1",
  clientId: "tradingview.com",
  userId: "public_user_id",
  timezone: "Asia/Jerusalem",
  fullscreen: false,
  theme: "Dark",
  autosize: true,
  studiesOverrides: {},
};

export default TVChartContainer;
